// Conservative adaptive render scale for touch devices. This changes only the
// WebGL backing buffer: CSS layout, HUD pixels and the 60 Hz simulation stay put.

const WARMUP_MS = 800;
const PRESSURE_FRAME_MS = 22;
const PRESSURE_HOLD_MS = 1_200;
const RECOVERY_FRAME_MS = 18;
const RECOVERY_HOLD_MS = 12_000;
const CHANGE_COOLDOWN_MS = 1_500;
const SAMPLE_CAP_MS = 100;
const EMA_WEIGHT = 0.08;

export function mobileResolutionSteps(nativeDpr: number): number[] {
  const safeDpr = Number.isFinite(nativeDpr) && nativeDpr > 0 ? nativeDpr : 1;
  const maximum = Math.min(safeDpr, 1.5);
  if (maximum <= 1) return [maximum];
  return [maximum, 1.25, 1]
    .filter((scale, index, values) => scale <= maximum && values.indexOf(scale) === index)
    .sort((a, b) => b - a);
}

export interface QualitySnapshot {
  ratio: number;
  maximum: number;
  minimum: number;
  level: number;
  levels: number;
  emaFrameMs: number;
  pressureMs: number;
  recoveryMs: number;
  active: boolean;
}

export class AdaptiveResolution {
  private readonly levels: number[];
  private level = 0;
  private emaFrameMs = 0;
  private warmupMs = 0;
  private pressureMs = 0;
  private recoveryMs = 0;
  private cooldownMs = 0;
  private active = false;

  constructor(levels: readonly number[]) {
    this.levels = [...new Set(levels)]
      .filter((scale) => Number.isFinite(scale) && scale > 0)
      .sort((a, b) => b - a);
    if (this.levels.length === 0) throw new Error('AdaptiveResolution needs at least one scale');
  }

  get ratio(): number {
    return this.levels[this.level];
  }

  snapshot(): QualitySnapshot {
    return {
      ratio: this.ratio,
      maximum: this.levels[0],
      minimum: this.levels[this.levels.length - 1],
      level: this.level,
      levels: this.levels.length,
      emaFrameMs: this.emaFrameMs,
      pressureMs: this.pressureMs,
      recoveryMs: this.recoveryMs,
      active: this.active,
    };
  }

  /** Returns a new DPR only when the backing buffer should be resized. */
  observe(frameMs: number, active: boolean): number | null {
    if (!active) {
      if (this.active) this.resetWindow();
      this.active = false;
      return null;
    }
    if (!this.active) {
      this.resetWindow();
      this.active = true;
    }
    if (!Number.isFinite(frameMs) || frameMs <= 0) return null;
    // One debugger stop or decode hitch cannot dominate a multi-second trend,
    // but a device that repeatedly takes 250ms still needs the 1x escape hatch.
    frameMs = Math.min(frameMs, SAMPLE_CAP_MS);

    this.emaFrameMs = this.emaFrameMs === 0
      ? frameMs
      : this.emaFrameMs + (frameMs - this.emaFrameMs) * EMA_WEIGHT;
    this.warmupMs += frameMs;
    if (this.warmupMs < WARMUP_MS) return null;

    if (this.cooldownMs > 0) {
      this.cooldownMs = Math.max(0, this.cooldownMs - frameMs);
      return null;
    }

    if (this.emaFrameMs > PRESSURE_FRAME_MS) {
      this.pressureMs = Math.min(PRESSURE_HOLD_MS, this.pressureMs + frameMs);
      this.recoveryMs = 0;
    } else if (this.emaFrameMs < RECOVERY_FRAME_MS) {
      this.recoveryMs = Math.min(RECOVERY_HOLD_MS, this.recoveryMs + frameMs);
      this.pressureMs = Math.max(0, this.pressureMs - frameMs * 2);
    } else {
      // The broad 45–55 FPS middle is acceptable and deliberately sticky.
      this.pressureMs = Math.max(0, this.pressureMs - frameMs * 0.5);
      this.recoveryMs = Math.max(0, this.recoveryMs - frameMs);
    }

    if (this.pressureMs >= PRESSURE_HOLD_MS && this.level < this.levels.length - 1) {
      this.level++;
      this.afterChange();
      return this.ratio;
    }
    if (this.recoveryMs >= RECOVERY_HOLD_MS && this.level > 0) {
      this.level--;
      this.afterChange();
      return this.ratio;
    }
    return null;
  }

  private afterChange(): void {
    this.pressureMs = 0;
    this.recoveryMs = 0;
    this.cooldownMs = CHANGE_COOLDOWN_MS;
  }

  private resetWindow(): void {
    this.emaFrameMs = 0;
    this.warmupMs = 0;
    this.pressureMs = 0;
    this.recoveryMs = 0;
    this.cooldownMs = 0;
  }
}
